/**
 * CommercialStandingGate — Foundation F's MINIMAL `/app` guard.
 *
 * Task 298. Specification:
 * `tasks/295A-commercial-foundation-and-staging.md` §8.1, §9.3, §9.4.1.
 * Task file: `tasks/298-commercial-standing-foundation.md` §7.5.
 *
 * ---------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL. Foundation F is the PR that turns the
 * commercial gate on. A foundation that denies every operation and routes
 * the user nowhere produces an unexplained dead end, which reads as "the
 * product is broken", not "you are not activated yet". A route nothing
 * redirects to is not a fallback — so F ships exactly ONE guard change:
 * a non-operational tenant reaching `/app` is routed to
 * `ActivationPendingPage`.
 *
 * OWNERSHIP IS SEQUENTIAL, NOT SHARED. After F merges, Lane O (Task 299)
 * owns this guard and `ActivationPendingPage` OUTRIGHT and replaces both
 * with the full pending / suspended / terminated journey (295A §8.2
 * criterion 2, §9.3). No other lane may touch either.
 *
 * IT RESOLVES STANDING FROM THE ACTIVATION-STATUS BOUNDARY ONLY — never
 * from a permission key, and never from `me/context`, which is NOT
 * widened by this task (295A §6.1.1). A guard keyed on a permission would
 * reintroduce the exemption model §5.4.1 explicitly rejects.
 *
 * ---------------------------------------------------------------------
 * THE STAGE-1 TRANSPORT RULE — STATED EXPLICITLY BECAUSE IT LOOKS LIKE A
 * FAIL-OPEN AND IS NOT.
 *
 * Between the merge (stage 1) and the Edge Function deploy (stage 3) the
 * activation-status endpoint DOES NOT EXIST. This guard must therefore
 * distinguish two different things:
 *
 *   - the endpoint ANSWERED and said non-operational → redirect to the
 *     fallback;
 *   - the endpoint COULD NOT BE REACHED AT ALL (404 because it is not
 *     deployed, or a network failure) → NO OPINION, LET THE USER
 *     THROUGH.
 *
 * The second is the only admissible behaviour, for two mechanical
 * reasons. Treating "unreachable" as "denied" would redirect EVERY
 * tenant, the live production tenant included, to a pending screen while
 * the database is still granting everything — an outage manufactured by a
 * screen. And the frontend is NEVER THE CONTROL (`SECURITY.md §9`):
 * letting a user through a frontend guard grants nothing, because the
 * database and the Edge Functions decide, and until stage 6 they still
 * grant exactly what `master` grants.
 *
 * Invariant A2's fail-closed-by-absence rule is about a MISSING STANDING
 * ROW IN THE DATABASE, where it is the control. It is not about a missing
 * HTTP endpoint in front of a database that has not been gated yet.
 *
 * A `permission_denied` refusal is ALSO no-opinion, and for a different
 * reason: the boundary refuses every CLIENT-CATEGORY actor in every state
 * (295A §5.4), so a refusal is not a statement about standing. A client
 * user must learn nothing about their agency's commercial standing; their
 * generic unavailability screen is Lane O's criterion 5, not F's.
 *
 * `ActivationPendingPage.test.ts` asserts BOTH branches so the rule
 * cannot rot into a genuine fail-open later.
 * ---------------------------------------------------------------------
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { isApiError } from '../../api/client';
import { getOrganizationActivationStatus } from '../../api/endpoints';
import type { CommercialState } from '../../api/types';
import {
  MAX_AUTOMATIC_REHYDRATIONS,
  claimRehydrationAttempt,
  rehydrationAttemptsFor,
  releaseRehydrationBudget,
} from '../../auth/activationRehydrationBudget';
import { isNonOperationalContext, useMeContext } from '../../context/MeContextProvider';
import { isClientOnly } from '../../permissions';
import { ActivationPendingPage } from '../../pages/ActivationPendingPage';
import { ErrorState, SessionTransitionState } from '../states';

/**
 * `unknown` is a first-class outcome, not an error bucket. See the
 * transport rule above.
 */
export type StandingProbe =
  | { readonly kind: 'loading' }
  | { readonly kind: 'operational' }
  | { readonly kind: 'not_operational'; readonly state: CommercialState }
  | { readonly kind: 'refreshing_context' }
  | { readonly kind: 'unknown' };

/**
 * Pure classifier, exported so the tests exercise the transport rule
 * directly rather than through a rendered tree.
 *
 * - a resolved payload  → operational / not_operational;
 * - `permission_denied` → unknown (a refusal is not a standing);
 * - `not_found`, any other API error, any network error → unknown
 *   (the endpoint is not deployed, or transport failed).
 */
export function classifyStandingResponse(
  result: { readonly commercial_state: CommercialState } | null,
  error: unknown,
): StandingProbe {
  if (result) {
    return result.commercial_state === 'activated'
      ? { kind: 'operational' }
      : { kind: 'not_operational', state: result.commercial_state };
  }
  // Every failure mode is deliberately collapsed to "no opinion". The
  // database is the control; a frontend guard that guesses on a failed
  // read can only ever manufacture an outage.
  void error;
  return { kind: 'unknown' };
}

/**
 * Task 306A §4 — is the context in hand incapable of rendering the
 * product the standing boundary just said is available?
 *
 * TRUE only when the probe said `activated` AND the context is still the
 * non-operational snapshot (no organization, no permissions — see
 * {@link isNonOperationalContext}). That combination has one ordinary
 * cause: the tenant was activated after this context was read.
 *
 * It can only ever DELAY `children`. Pure and exported so the behaviour
 * below is executed by test.
 */
export function needsContextRehydration(
  probe: StandingProbe,
  me: { readonly system_admin: boolean; readonly organization: unknown } | null,
): boolean {
  if (probe.kind !== 'operational') return false;
  return isNonOperationalContext(me as never);
}

/**
 * What the gate renders, as one pure decision.
 *
 * EXTRACTED SO THE WHOLE TRANSITION IS EXECUTABLE. The binding review of
 * the first version was right that source pins over this logic prove
 * nothing: they cannot show what happens when a rehydration comes back
 * still non-operational, which is precisely the case where the first
 * design silently reopened the blank product.
 *
 * Ordering is load-bearing and is asserted:
 *
 *   1. probe in flight            → `hold`
 *   2. explicitly not operational → `pending` (the activation journey)
 *   3. operational but the context cannot render it → `rehydrate` while
 *      budget remains, `stalled` once it is spent. NEVER `admit`.
 *   4. client-category actor stripped to nothing on an `unknown` probe →
 *      `client_unavailable`
 *   5. everything else            → `admit`
 *
 * `unknown` still admits, because it means the endpoint could not be
 * reached and the database is the control (see the transport rule above).
 * It reaches step 3 only if the context is non-operational, and step 3
 * requires `operational`, so an unreachable endpoint can never produce a
 * hold.
 */
export type GateDecision =
  | { readonly kind: 'hold' }
  | { readonly kind: 'rehydrate' }
  | { readonly kind: 'stalled' }
  | { readonly kind: 'pending'; readonly state: CommercialState }
  | { readonly kind: 'client_unavailable' }
  | { readonly kind: 'admit' };

export function decideGate(input: {
  readonly probe: StandingProbe;
  readonly me: { readonly system_admin: boolean; readonly organization: unknown } | null;
  readonly attempts: number;
  readonly clientStrippedOfEverything: boolean;
  readonly maxAttempts?: number;
}): GateDecision {
  const { probe, me, attempts, clientStrippedOfEverything } = input;
  const maxAttempts = input.maxAttempts ?? MAX_AUTOMATIC_REHYDRATIONS;

  if (probe.kind === 'loading' || probe.kind === 'refreshing_context') return { kind: 'hold' };
  if (probe.kind === 'not_operational') return { kind: 'pending', state: probe.state };

  if (needsContextRehydration(probe, me)) {
    return attempts < maxAttempts ? { kind: 'rehydrate' } : { kind: 'stalled' };
  }

  if (probe.kind === 'unknown' && clientStrippedOfEverything) {
    return { kind: 'client_unavailable' };
  }

  return { kind: 'admit' };
}

export function CommercialStandingGate({ children }: { readonly children: ReactNode }) {
  const { me, status: meStatus, refresh: refreshMe } = useMeContext();
  const [probe, setProbe] = useState<StandingProbe>({ kind: 'loading' });
  const [probeKey, setProbeKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    getOrganizationActivationStatus({ signal: controller.signal })
      .then((data) => {
        if (cancelled) return;
        const classified = classifyStandingResponse(data, null);
        if (probeKey > 0 && classified.kind === 'operational') {
          setProbe({ kind: 'refreshing_context' });
          refreshMe();
          return;
        }
        setProbe(classified);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // `isApiError` is imported so a future reader can see that the
        // typed-envelope path was considered; every branch resolves to
        // `unknown` on purpose.
        void isApiError(err);
        setProbe(classifyStandingResponse(null, err));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [probeKey, refreshMe]);

  useEffect(() => {
    if (probe.kind === 'refreshing_context' && meStatus === 'ready') {
      setProbe({ kind: 'operational' });
    }
  }, [meStatus, probe.kind]);

  const refreshStanding = useCallback(() => {
    setProbe({ kind: 'loading' });
    setProbeKey((current) => current + 1);
  }, []);

  // Task 306A §4.2 — the deterministic rehydration transition.
  //
  // The explicit «تحقق من حالة التفعيل» button already routed through
  // `refreshing_context`, because it calls `refreshMe()` itself. The path
  // this budget repairs is the one with no click in it: a reload, a new tab,
  // or returning to a session that was open before the operator activated.
  // The probe answers `activated` on the FIRST pass (`probeKey === 0`), so
  // the old code admitted straight through with whatever context happened to
  // be in hand.
  //
  // §4.4 CORRECTS THE OTHER HALF OF THAT SENTENCE. An earlier version said
  // the button «was never the broken path». Staging browser QA on
  // `ff06d07` disproved it: the click routed correctly and still produced
  // a document with `body.innerText.length === 0` for ~1.2 s, because every
  // surface it routes THROUGH rendered a title-less skeleton. Routing
  // correctly and rendering something are two claims, and only the first
  // was ever true here.
  //
  // The budget lives in `auth/activationRehydrationBudget` — outside React —
  // because `refreshMe()` unmounts this component (see that module's
  // header).
  //
  // `bumpBudget` is DEFENSIVE, not load-bearing, and the distinction is
  // written down because an earlier comment overclaimed it. In practice
  // `refreshMe()` always moves `me/context` to `loading`, React 18 batches
  // that with this setter, and `AuthGate` replaces this subtree with its
  // skeleton in the resulting commit — so the local update is normally
  // discarded during unmount. It is kept so this component's termination
  // does not depend on a property of another module: if a refresh ever
  // failed to change the status, the decision would still be recomputed
  // here rather than leaving the spinner up forever. Only the SETTER is
  // used; reading the counter would claim the state lives in React.
  const [, bumpBudget] = useState(0);
  // Always a string when `me` exists — `buildMeContext` sets
  // `actor.user_id` unconditionally — and `me` always exists by the time a
  // rehydration is possible, because `isNonOperationalContext(null)` is
  // false. The null branch is therefore unreachable and typed, not handled.
  const userId = me?.actor.user_id ?? null;

  // The standing endpoint deliberately refuses client-category actors, so an
  // unknown response cannot identify a suspended agency. Task 298 instead
  // strips their effective permissions. Render one generic unavailable state
  // without naming the agency's commercial standing.
  const clientStrippedOfEverything = Boolean(me && isClientOnly(me) && me.permissions.length === 0);

  const decision = decideGate({
    probe,
    me,
    attempts: userId ? rehydrationAttemptsFor(userId) : 0,
    clientStrippedOfEverything,
  });

  // THE CLAIM IS WHAT DECIDES, NOT THE DECISION. `main.tsx` mounts inside
  // `<StrictMode>`, and React re-invokes mount effects in development with
  // the SAME render closure — so `decision.kind` is still `rehydrate` on the
  // replay. A check-then-act spend fires twice; an atomic claim lets the
  // replay lose.
  //
  // Sign-out is NOT handled here: this gate lives inside `AuthGate`, which
  // stops rendering children the moment the status is `unauthenticated`, so
  // an effect here would never observe it. `MeContextProvider` releases the
  // budget instead.
  useEffect(() => {
    if (decision.kind !== 'rehydrate' || !userId) return;
    if (!claimRehydrationAttempt(userId, MAX_AUTOMATIC_REHYDRATIONS)) return;
    bumpBudget((n) => n + 1);
    refreshMe();
  }, [decision.kind, userId, refreshMe]);

  /** Human-initiated, so it cannot run away: one click, one more attempt. */
  const retryRehydration = useCallback(() => {
    if (userId) releaseRehydrationBudget(userId);
    bumpBudget((n) => n + 1);
    refreshMe();
  }, [refreshMe, userId]);

  if (decision.kind !== 'admit') {
    return (
      <CommercialStandingGateSurface
        decision={decision}
        probeKind={probe.kind}
        onRetryRehydration={retryRehydration}
        onRefreshStanding={refreshStanding}
      />
    );
  }

  // `operational` and `unknown` both render the product. See the
  // transport rule: `unknown` grants nothing, because the database and
  // the Edge Functions are what decide.
  return <>{children}</>;
}

/**
 * Task 306A §4.4 — the two sentences this gate holds on.
 *
 * They are exported CONSTANTS rather than JSX literals so the regression
 * suite asserts the copy that the rendered surface actually carries, and so
 * a caller cannot pass an empty string into the transition surface here
 * without the pin moving with it.
 */
export const STANDING_TRANSITION_COPY = {
  /** Standing answered `activated` and the context is being re-read. */
  rehydrating: {
    title: 'تم التفعيل، جاري تحديث مساحة العمل…',
    description: 'حالة الحساب مفعّلة. نقرأ صلاحيات الحساب الآن قبل فتح مساحة العمل.',
  },
  /** The standing probe itself is in flight. */
  checking: {
    title: 'جاري التحقق من حالة الحساب…',
    description: 'نقرأ حالة حساب الوكالة قبل عرض مساحة العمل.',
  },
} as const;

/**
 * EVERY surface this gate can render that is NOT the product, as one pure
 * prop-driven component.
 *
 * WHY IT IS EXTRACTED. Task 306A §4.4. `renderToStaticMarkup` does not run
 * effects, so the gate mounted whole can only ever be observed in its
 * FIRST state — and the staging defect this closes lived in the states
 * after it. Prop-driven, every frame of the pending_activation → activated
 * transition is rendered by the regression suite and read for visible text,
 * which is the property that failed on staging and the one a source pin
 * could never have stated.
 *
 * IT CANNOT ADMIT. `children` is not a prop of this component and
 * `decision` excludes `admit` by type, so nothing here is a route into the
 * product. The gate keeps that decision, and it is still the only
 * `<>{children}</>` in this file.
 *
 * The fall-through is FAIL-SAFE and unreachable by type: a decision kind
 * added later without a branch here renders the neutral checking surface
 * rather than `null`, because a `null` render is the defect §4.4 exists to
 * remove.
 */
export function CommercialStandingGateSurface({
  decision,
  probeKind,
  onRetryRehydration,
  onRefreshStanding,
}: {
  readonly decision: Exclude<GateDecision, { readonly kind: 'admit' }>;
  readonly probeKind: StandingProbe['kind'];
  readonly onRetryRehydration: () => void;
  readonly onRefreshStanding: () => void;
}) {
  if (decision.kind === 'hold' || decision.kind === 'rehydrate') {
    // NOT `LoadingState`: its default `page` variant accepts a title and
    // renders no text at all, so this surface — the one an owner watches
    // for the whole transition — was a blank document (§4.4).
    const copy =
      decision.kind === 'rehydrate' || probeKind === 'refreshing_context'
        ? STANDING_TRANSITION_COPY.rehydrating
        : STANDING_TRANSITION_COPY.checking;
    return (
      <SessionTransitionState
        title={copy.title}
        description={copy.description}
        testId="commercial-standing-transition"
      />
    );
  }

  // The automatic attempt is spent and the context still cannot render the
  // product. This is a BLOCKING state on purpose: admitting here would put
  // the owner back in front of the empty shell this task exists to remove,
  // and would do it silently. The copy states the two facts that are
  // actually known — the account is active, the workspace did not load —
  // and offers the one action that can help.
  if (decision.kind === 'stalled') {
    return (
      <div style={{ padding: 32 }}>
        <ErrorState
          title="تم تفعيل الحساب، لكن تعذّر تحميل مساحة العمل"
          description="حالة الحساب مفعّلة، لكن بيانات الصلاحيات لم تصل بعد. أعد المحاولة، وإذا تكررت المشكلة تواصل مع فريق المنصة."
          onRetry={onRetryRehydration}
        />
      </div>
    );
  }

  if (decision.kind === 'pending') {
    return (
      <ActivationPendingPage
        commercialState={decision.state}
        onRefresh={onRefreshStanding}
        refreshing={false}
      />
    );
  }

  if (decision.kind === 'client_unavailable') {
    return <ActivationPendingPage clientUnavailable />;
  }

  return (
    <SessionTransitionState
      title={STANDING_TRANSITION_COPY.checking.title}
      description={STANDING_TRANSITION_COPY.checking.description}
      testId="commercial-standing-transition"
    />
  );
}
